import { useState, type ReactNode } from "react";
import { BRAND, PRIVACY, VISIT_COUNTING } from "../design/copy.js";
import { BloomBackground } from "../ui/background.js";
import { Rule } from "../ui/primitives.js";
import { browserAsksNotToCount, doNotCount, setDoNotCount } from "../data/visit-count.js";

/** The section the visit-counting switch sits under (ADR 0022). */
const COUNTING_SECTION = "Counting visits.";
/** The section whose last sentence links to the terms (2026-10-22, the controller line). */
const RESPONSIBLE_SECTION = "Who is responsible.";
/** The section the region gate's sentence ends, with DB-IP's credit linked (2026-10-27). */
const COLLECT_SECTION = "What we collect.";

/**
 * The privacy policy. Same card-on-bloom composition as the auth screen it is
 * reached from, wider because it is a reading surface; below the `sm`
 * breakpoint the card is the whole width, so a phone gets no sideways scroll.
 * Reachable cold — a
 * learner can be handed this URL before they have an account, which is the
 * point of it.
 *
 * Every word rendered here comes from PRIVACY in design/copy.ts verbatim; the
 * truthfulness rules live on that block. This file is only layout, and the
 * one control the page carries: "don't count my visits." under "Counting
 * visits." (VISIT_COUNTING, ADR 0022). The first section, "Who is
 * responsible." (2026-10-22), ends on the line that links to the terms page,
 * which links back here. "What we collect." ends on the region gate's sentence
 * (2026-10-27), whose DB-IP credit links to db-ip.com, as the licence asks.
 * Its last control goes back where the app says (the
 * review of 2026-10-22: the exact address the reading began from), in the
 * word the app chooses: "back to sign in." when that is where it goes.
 */
export function Privacy({ onBack, backWord = PRIVACY.backToSignIn }: { onBack: () => void; backWord?: string }): ReactNode {
  return (
    <div className="relative flex min-h-svh items-start justify-center sm:py-16">
      <BloomBackground />
      <div
        data-testid="privacy-card"
        className="relative z-10 w-full bg-paper px-6 py-10 sm:w-[44%] sm:min-w-[34rem] sm:max-w-[46rem] sm:px-16 sm:py-12"
      >
        <div className="font-sans text-nav">{BRAND.short}</div>
        <Rule className="mt-2" />

        <h1 className="mt-6 mb-0 font-display text-display font-bold">{PRIVACY.title}</h1>
        <div className="mt-2 font-sans text-micro">{PRIVACY.updated}</div>

        <p className="mt-8 mb-0 font-sans text-body">{PRIVACY.intro}</p>
        <Rule className="mt-6" />

        {PRIVACY.sections.map((section) => (
          <section key={section.heading}>
            <h2 className="mt-8 mb-0 font-display text-nav font-bold">{section.heading}</h2>
            <p className="mt-2 mb-0 font-sans text-body">{section.body}</p>
            {"more" in section
              ? section.more.map((paragraph) => (
                  <p key={paragraph} className="mt-4 mb-0 font-sans text-body">
                    {paragraph}
                  </p>
                ))
              : null}
            {section.heading === RESPONSIBLE_SECTION ? (
              <p className="mt-4 mb-0 font-sans text-body">
                {PRIVACY.termsLine.before}
                <a href="#/terms" className="text-link underline">
                  {PRIVACY.termsLine.link}
                </a>
                {PRIVACY.termsLine.after}
              </p>
            ) : null}
            {section.heading === COLLECT_SECTION ? (
              <p className="mt-4 mb-0 font-sans text-body">
                {PRIVACY.regionCheck.before}
                <a href={PRIVACY.regionCheck.credit.href} target="_blank" rel="noopener noreferrer" className="text-link underline">
                  {PRIVACY.regionCheck.credit.link}
                </a>
                {PRIVACY.regionCheck.after}
              </p>
            ) : null}
            {section.heading === COUNTING_SECTION ? <VisitCountingSwitch /> : null}
          </section>
        ))}

        <Rule className="mt-10" />
        <button
          type="button"
          onClick={onBack}
          className="mt-6 w-fit cursor-pointer border-0 bg-transparent p-0 text-left font-sans text-nav text-ink hover:bg-marker"
        >
          {backWord}
        </button>
      </div>
    </div>
  );
}

/**
 * "don't count my visits." — a switch whose ON state means opted out. It
 * keeps one flag in this browser and sends nothing (data/visit-count.ts).
 * Global Privacy Control and Do Not Track take precedence: when either is
 * on, the browser-signal line shows whatever the switch says. A confirmation
 * shows only once the preference has actually been saved.
 */
export function VisitCountingSwitch(): ReactNode {
  const [on, setOn] = useState(() => doNotCount());
  const [said, setSaid] = useState<"savedOn" | "savedOff" | "saveFailed" | null>(null);
  const signal = browserAsksNotToCount();

  const toggle = (): void => {
    const next = !on;
    if (setDoNotCount(next)) {
      setOn(next);
      setSaid(next ? "savedOn" : "savedOff");
    } else {
      setSaid("saveFailed");
    }
  };

  return (
    <div data-testid="visit-counting" className="mt-6 font-sans">
      <button
        type="button"
        role="switch"
        aria-checked={on}
        onClick={toggle}
        className="flex cursor-pointer items-baseline gap-3 border-0 bg-transparent p-0 text-left font-sans text-body text-ink hover:bg-marker"
      >
        <span>{VISIT_COUNTING.label}</span>
        <span className="text-small text-ink-soft">{on ? VISIT_COUNTING.on : VISIT_COUNTING.off}</span>
      </button>
      <p className="mt-2 mb-0 text-small text-ink-soft">{VISIT_COUNTING.browserOnly}</p>
      {signal ? <p className="mt-2 mb-0 text-small">{VISIT_COUNTING.browserSignal}</p> : null}
      {said === null ? null : (
        <p role="status" className="mt-2 mb-0 text-small">
          {VISIT_COUNTING[said]}
        </p>
      )}
    </div>
  );
}
